import {
  AbsenceReason,
  AbsenceStatus,
  AssignedTimeDto,
  AvailabilityDto,
  TranslationLookups,
} from 'api/model';
import DatePicker from 'components/inputs/DatePicker';
import {
  DEFAULT_MAX_HOURS_PER_4_WEEKS,
  DEFAULT_MAX_HOURS_PER_WEEK,
} from 'constants/AppConstants';
import { getDrivingBarHours } from './availability-helper';
import TextArea from 'components/inputs/TextArea';
import Modal from 'components/Modal';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faChevronLeft,
  faChevronRight,
  faInfoCircle,
  faTimes,
  faTrash,
  faWarning,
} from '@fortawesome/free-solid-svg-icons';
import AuthorizedButton from 'components/inputs/AuthorizedButton';
import {
  formatDate,
  formatDateTime,
  formatTime,
  getHoursAndMinutesFromMillisecondsString,
  toDateFromHHmm,
} from 'utils/date-helpers';
import TextButton from 'components/inputs/TextButton';
import useTranslations from 'contexts/basicData/useTranslations';
import { amber, lightGreen, lightGrey, lightRed } from 'constants/Colors';
import Checkbox from 'components/inputs/Checkbox';
import AbsenceReasonIcon from './AbsenceReasonIcon';
import SearchableSelect, {
  SearchableSelectItem,
  SelectOption,
} from 'components/inputs/SearchableSelect';
import useModalStack from 'contexts/modal/useModalStack';
import useUsers from 'contexts/basicData/useUsers';
import toast from 'react-hot-toast';
import {
  getDrivingPolicyGetDrivingReportsQueryKey,
  useDrivingPolicyGetAssignedTimesForUser,
} from 'api/driving-policy/driving-policy';
import AssignedTimesTimeline from 'components/AssignedTimesTimeline';
import Table from 'components/Table';
import { ProgressBarWithTooltip, RowCss } from '../DrivingPolicy';
import { ColumnSetting } from 'components/Table/utils';
import Button from 'components/inputs/Button';
import WorkTimeWidget from './WorkTimeWidget';
import WorkDayMap from './WorkDayMap';
import WeekAvailabilityOverview from './WeekAvailabilityOverview';
import {
  useAssignmentDebugRunAutoAnalysisForFTDay,
  useAssignmentGetBookedAssignmentsForUserAndDate,
} from 'api/assignment/assignment';
import {
  getUserGetEmployeeAvailabilityForDateQueryKey,
  useUserDeleteAvailability,
  useUserGetEmployeeAvailabilityForDate,
  useUserGetUserById,
  useUserUpdateUserAvailability,
} from 'api/user/user';
import { useQueryClient } from '@tanstack/react-query';
import { showException } from 'utils/exception-helper';
import { makeNumberComparator } from 'utils/sorting';
import MediaQuery from 'constants/MediaQuery';
import useMe from 'contexts/authentication/useMe';
import Roles, { includesRoles } from 'constants/Roles';

const Container = styled.div`
  gap: 20px;
  padding-bottom: 15px;
  display: grid;
  grid-template-columns: 1fr;
  ${MediaQuery.laptopL} {
    grid-template-columns: 800px 700px;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  gap: 20px;
  padding: 10px;
  padding-right: 0px;
`;

const DrivingTimesContainer = styled.div`
  border-left: 1px solid ${({ theme }) => theme.colors.border.primary};
  display: flex;
  flex-direction: column;
  align-self: flex-start;
`;

// The timeline view is a fixed 500px scroll viewport (its slots are pixel-width per
// duration and scroll horizontally). The map, however, should fill the whole pane —
// so in map mode we drop the 500px cap and let it take the column width.
const TimeLineContainer = styled.div<{ $fillWidth?: boolean }>`
  ${({ $fillWidth }) =>
    $fillWidth
      ? css`
          width: 100%;
        `
      : css`
          max-width: 500px;
          min-width: 500px;
        `}
  overflow-x: auto;
  overflow-y: auto;
`;

const BoldSpan = styled.span`
  font-weight: bold;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  gap: 10px;
  user-select: none;
  align-items: center;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
  gap: 10px;
`;

const InputButton = styled.button`
  width: 120px;
  height: 35px;
  text-align: center;
  padding-top: 4px;
  padding-bottom: 4px;
  padding-right: 4px;
  border: none;
  border-radius: 3px;
`;

const Label = styled.div`
  margin-bottom: 5px;
  min-height: 1em;
  padding-left: 3px;
`;

const Title = styled.h2`
  margin-top: 15px;
  font-size: ${({ theme }) => theme.sizes.font.medium};

  &:first-of-type {
    margin-top: 0;
  }
`;

const RestrictionBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid ${amber};
  border-left: 4px solid ${amber};
  background: rgba(255, 191, 0, 0.12);
  border-radius: 4px;
  font-size: ${({ theme }) => theme.sizes.font.small};
`;

const TableRowCellWraper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

const AbsenceButton = styled(AuthorizedButton)<{
  selected?: boolean;
  colorSelected?: string;
  colorDeselected?: string;
}>`
  flex: 1;
  margin-left: 0;
  margin-right: auto;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.foreground.primary};
  ${({ selected, colorSelected, colorDeselected, theme }) =>
    selected
      ? css`
          background: ${colorSelected || theme.colors.background.selection};
        `
      : css`
          background: ${colorDeselected || theme.colors.background.button};
        `}
`;

const CustomInput = forwardRef((props: any, ref) => (
  <InputButton {...props} ref={ref} style={{ cursor: 'pointer' }} type="button">
    {props.value}
  </InputButton>
));

const generateDefaultValues = (availability?: AvailabilityDto) => {
  if (!availability) return {};
  return {
    // user: availability.user,
    userID: availability.userID,
    // date: availability.date,
    dateString: availability.dateString,
    startString:
      availability.absenceReason !== null
        ? undefined
        : (availability.startString ?? availability.originalTimes?.startString),
    endString:
      availability.absenceReason !== null
        ? undefined
        : (availability.endString ?? availability.originalTimes?.endString),
    absenceStatus: availability.absenceStatus ?? undefined,
    absenceReason: availability.absenceReason ?? undefined,
    comment: availability.comment,
    adminComment: availability.adminComment,
    isFullyBooked: availability.isFullyBooked ?? false,
    scheduleStatus: availability.scheduleStatus,
    isOnlyAvailableForFurtherTransport:
      availability.isOnlyAvailableForFurtherTransport ?? false,
  };
};

type AvailabilityFormModalProps = {
  viewOnly?: boolean;
  /** Allow editing the "Begränsa bokningar" section even when viewOnly is true */
  allowBookingRestrictions?: boolean;
  name: string;
  userId: number;
  availability?: AvailabilityDto;
  showUserFields?: boolean;
  initialDate?: Date;
  onClose?(): void;
  onSaved?(): void;
};

// Äldre rader saknar Created (DateTimeOffset.MinValue) — fall tillbaka på CreatedAt.
const isValidAuditDate = (value?: Date | string) =>
  !!value && new Date(value).getFullYear() > 2000;

const AbsenceRequestTimestamps = ({
  availability,
}: {
  availability: AvailabilityDto;
}) => {
  const users = useUsers();
  const created = isValidAuditDate(availability.created)
    ? availability.created
    : isValidAuditDate(availability.createdAt)
      ? availability.createdAt
      : undefined;
  const modified = isValidAuditDate(availability.modified)
    ? availability.modified
    : undefined;
  const userName = (id?: number) => (id ? users[id]?.name : undefined);
  const createdBy = userName(availability.createdByID);
  const modifiedBy = userName(availability.modifiedByID);

  return (
    <Col>
      <Row>
        <BoldSpan>Begäran skapad:</BoldSpan>
        {`${created ? formatDateTime(new Date(created)) : '-'}${
          createdBy ? ` (${createdBy})` : ''
        }`}
      </Row>
      <Row>
        <BoldSpan>Senast uppdaterad:</BoldSpan>
        {`${modified ? formatDateTime(new Date(modified)) : '-'}${
          modifiedBy ? ` (${modifiedBy})` : ''
        }`}
      </Row>
    </Col>
  );
};

const AvailabilityFormModal = ({
  name,
  userId,
  availability: propsAvailability,
  onClose,
  onSaved,
  showUserFields = true,
  viewOnly,
  allowBookingRestrictions,
  initialDate,
}: AvailabilityFormModalProps) => {
  const queryClient = useQueryClient();
  const users = useUsers();
  const me = useMe();
  const canSeeAbsenceRequestTimestamps = includesRoles(
    [Roles.Ekonomi, Roles.SuperAdmin],
    me?.roles
  );
  const getUserByIdCall = useUserGetUserById(
    {
      userId,
    },
    {
      query: {
        enabled: userId !== undefined,
      },
    }
  );
  const modalStack = useModalStack();
  const { absenceReasons, absenceStatuses } = useTranslations();

  const [rightPaneView, setRightPaneView] = useState<'timeline' | 'map'>('map');
  const [currentDate, setCurrentDate] = useState<Date>(
    initialDate ??
      (propsAvailability?.date ? new Date(propsAvailability.date) : new Date())
  );

  // Dev-only: triggers the daily auto-validation snurra (rule-based + AI)
  // for this FT's assignments on the current date. Lets us validate that
  // the snurra correctly picks up standardized comments and InvoiceExtra
  // rows that would otherwise only land after the 15-min Azure Function
  // tick + the 1h "completed-grace" delay.
  const isDev = process.env.NODE_ENV === 'development';
  const debugRunSnurraMutation = useAssignmentDebugRunAutoAnalysisForFTDay();
  const handleDebugRunSnurra = async () => {
    try {
      const result = await debugRunSnurraMutation.mutateAsync({
        params: { userId, date: currentDate },
      });
      // Orval typed the response as Blob since the controller returns IActionResult
      // (no [ProducesResponseType]). Read the text and parse the summary JSON.
      const text = await (result as unknown as Blob).text();
      const json = JSON.parse(text) as {
        totalAssignments: number;
        aiAnalyzed: number;
      };
      toast.success(
        `Snurran körd: ${json.totalAssignments} uppdrag, AI-analys på ${json.aiAnalyzed}.`
      );
    } catch (e) {
      toast.error('Kunde inte trigga snurran');
    }
  };

  const tomorrow = useMemo(
    () =>
      new Date(
        new Date(currentDate).setDate(new Date(currentDate).getDate() + 1)
      ),
    [currentDate]
  );

  const availabilityCall = useUserGetEmployeeAvailabilityForDate(
    {
      userId,
      date: currentDate,
    },
    {
      query: {
        enabled: userId !== undefined,
        refetchOnWindowFocus: false,
      },
    }
  );

  const availability = availabilityCall.data ?? propsAvailability;

  // Booked assignments for the selected day. Owned here so both the
  // Arbetsdag-list in WorkTimeWidget and the map view share the same fetch.
  const bookedAssignmentsCall =
    useAssignmentGetBookedAssignmentsForUserAndDate();
  const refreshBookedAssignments = useCallback(async () => {
    if (!availability) return;
    try {
      await bookedAssignmentsCall.mutateAsync({
        params: { userId, date: availability.date },
      });
    } catch {
      // Errors surface via the modal's toast layer elsewhere; the hook
      // exposes .error if a caller needs more granular handling.
    }
  }, [bookedAssignmentsCall, availability, userId]);
  useEffect(() => {
    if (availability) {
      refreshBookedAssignments();
    }
    // Refetch only on user/date change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, availability?.dateString]);

  const goToPreviousDate = () => {
    setCurrentDate(new Date(currentDate.setDate(currentDate.getDate() - 1)));
  };

  const goToNextDate = () => {
    setCurrentDate(new Date(currentDate.setDate(currentDate.getDate() + 1)));
  };

  const { data: assignedTimes } = useDrivingPolicyGetAssignedTimesForUser(
    { userId, limit: 5, beforeTime: tomorrow }
    // { query: { enabled: false } }
  );

  const { register, handleSubmit, setValue, watch, reset } =
    useForm<AvailabilityDto>({
      defaultValues: generateDefaultValues(availability),
    });
  const startInputRef = useRef(null);
  const endInputRef = useRef(null);

  useEffect(() => {
    if (!availabilityCall.isSuccess) {
      return;
    }
    reset(generateDefaultValues(availabilityCall.data ?? undefined));
  }, [availabilityCall.isSuccess, availabilityCall.data, reset]);

  const absenceReasonOptions = Object.entries(absenceReasons)
    .filter(([reason]) => Number(reason) !== AbsenceReason.OTHER)
    .map(([reason, reasonName]) => ({
      key: reason,
      value: Number(reason),
      label: (
        <Row>
          <AbsenceReasonIcon
            absenceReason={Number(reason) as AbsenceReason}
            absenceReasons={absenceReasons}
            absenceStatuses={absenceStatuses}
          />
          {reasonName}
        </Row>
      ),
    }));

  const updateCall = useUserUpdateUserAvailability();
  const removeCall = useUserDeleteAvailability();

  const invalidateAvailabilityQuery = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: getUserGetEmployeeAvailabilityForDateQueryKey({
        userId,
        date: currentDate,
      }),
    });
    // Ensure updated form state after mutation
    const freshData = await availabilityCall.refetch();
    if (freshData.data) {
      reset(generateDefaultValues(freshData.data));
    }
  }, [queryClient, userId, currentDate, availabilityCall, reset]);

  const handleSaveClick = handleSubmit(async (data) => {
    if (!data) return;
    try {
      await updateCall.mutateAsync({
        data: {
          availability: { ...data, dateString: formatDate(currentDate) },
          userId,
        },
      });
      await invalidateAvailabilityQuery();
      // Invalidate driving policy reports to refresh absence hours in progress bars
      queryClient.invalidateQueries({
        queryKey: getDrivingPolicyGetDrivingReportsQueryKey(),
      });
      onSaved?.();
      modalStack.pop();
    } catch (error) {
      showException(error);
    }
  });

  const removeAvailability = async () => {
    try {
      await removeCall.mutateAsync({
        params: {
          availabilityId: availability?.id,
        },
      });
      await invalidateAvailabilityQuery();
      // Invalidate driving policy reports to refresh absence hours in progress bars
      queryClient.invalidateQueries({
        queryKey: getDrivingPolicyGetDrivingReportsQueryKey(),
      });
      onSaved?.();
      modalStack.pop();
    } catch (error) {
      showException(error);
    }
  };

  const handleCloseClick = () => {
    onClose?.();
  };

  const handleRemoveClick = async () => {
    modalStack.push(
      <Modal
        buttons={[
          {
            label: 'Ja',
            onClick: removeAvailability,
          },
          {
            label: 'Nej',
            onClick: () => modalStack.pop(),
          },
        ]}
        onClose={() => modalStack.pop()}
        title="Ta bort tillgänglighet"
      >
        <p>Är du säker på att du vill ta bort tillgängligheten?</p>
      </Modal>
    );
  };

  const renderAbsenceReasonOption = (
    key: string,
    option: SelectOption<number | undefined, AbsenceReason | undefined>,
    onClick: () => void
  ) => (
    <SearchableSelectItem key={key} onClick={onClick}>
      {option.label}
    </SearchableSelectItem>
  );

  const getColorForHoursDrivenLast24h = (hours: number) => {
    if (hours >= 0 && hours < 9) {
      return 'green';
    }
    if (hours >= 0 && hours < 13) {
      return 'rgb(255, 123, 0)';
    }
    return 'red';
  };

  const isFullTimeUser = users[userId]?.isFullTime ?? false;

  const columnSettings: ColumnSetting<AssignedTimeDto, TranslationLookups>[] =
    useMemo(() => {
      const columns: ColumnSetting<AssignedTimeDto, TranslationLookups>[] = [
        {
          head: 'Uppdrag',
          css: RowCss,
          render: (as) => (
            <TableRowCellWraper>
              {as.isAbsenceTime ? (
                <>
                  <span>
                    <b>{absenceReasons[as.absenceReason!]}</b>
                  </span>
                  <AbsenceReasonIcon
                    absenceReason={as.absenceReason}
                    absenceStatus={AbsenceStatus.APPROVED}
                    absenceReasons={absenceReasons}
                    absenceStatuses={absenceStatuses}
                  />
                </>
              ) : (
                <>
                  <b>{as.registrationNumber}</b>
                  <span>{as.assignmentId}</span>
                </>
              )}
            </TableRowCellWraper>
          ),
          width: 80,
        },
        {
          head: 'Start- och sluttid',
          css: RowCss,
          render: (as) => (
            <TableRowCellWraper>
              {' '}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  fontWeight: 'bold',
                }}
              >
                <div>{formatTime(as.startWorkTime)}</div>
                <div>{formatTime(as.endWorkTime)}</div>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <div>{formatDate(as.startWorkTime)}</div>
                <div>{formatDate(as.endWorkTime)}</div>
              </div>
            </TableRowCellWraper>
          ),
          width: 180,
        },
        {
          head: (
            <>
              <span>Körtid 24h</span>
              <FontAwesomeIcon
                icon={faInfoCircle}
                title="Beräknad körtid för uppdraget och tidigare
              uppdrag för 24h bakåt utifrån uppdragets sluttid."
              />
            </>
          ),
          css: RowCss,
          render: (as) => (
            <span>
              {as.report.hoursDrivenLast24h ? (
                <b
                  style={{
                    color: getColorForHoursDrivenLast24h(
                      as.report.hoursDrivenLast24h
                    ),
                  }}
                >
                  {getHoursAndMinutesFromMillisecondsString(
                    as.report.hoursDrivenLast24h * 60 * 60 * 1000
                  )}
                </b>
              ) : (
                '-'
              )}
            </span>
          ),
          width: 100,
        },
        {
          head: (
            <>
              <span>Arb. tid idag</span>
              <FontAwesomeIcon
                icon={faInfoCircle}
                title="Arbetad i kalendertid från första uppdraget innevarande dag fram till och med detta uppdrag."
              />
            </>
          ),
          css: RowCss,
          render: (as) => (
            <span>
              {as.report.workHoursToday ? (
                <b
                  style={{
                    color: getColorForHoursDrivenLast24h(
                      as.report.workHoursToday
                    ),
                  }}
                >
                  {getHoursAndMinutesFromMillisecondsString(
                    as.report.workHoursToday * 60 * 60 * 1000
                  )}
                </b>
              ) : (
                '-'
              )}
            </span>
          ),
          width: 100,
        },
        {
          head: (
            <span>
              Dygnsvila
              <FontAwesomeIcon
                icon={faInfoCircle}
                title="Status för om uppdraget håller sig inom tillåten samanhängande dygnsvila/arbetstidslängd."
              />
            </span>
          ),
          css: RowCss,
          render: (as) => (
            <FontAwesomeIcon
              icon={
                as.report.hasConsecutiveHoursOfRestLast24Hours
                  ? faCheck
                  : faWarning
              }
              style={{
                color: as.report.hasConsecutiveHoursOfRestLast24Hours
                  ? 'green'
                  : 'red',
              }}
              title={
                as.report.hasConsecutiveHoursOfRestLast24Hours
                  ? ''
                  : 'För lite sammanhängande dygnsvila eller för lång arbetsdag'
              }
            />
          ),
          width: 90,
        },
        {
          head: (
            <>
              <span style={{ marginRight: '5px' }}>Innevarande vecka</span>
              <FontAwesomeIcon
                icon={faInfoCircle}
                title="Beräknad körtid för veckan uppdraget körs, aktuella veckan utgår ifrån uppdragets sluttid."
              />
            </>
          ),
          css: RowCss,
          render: (as) => {
            const value = Math.max(as.report?.hoursDrivenThisWeek || 0, 0);
            const absenceValue = as.report?.absenceHoursThisWeek || 0;
            const maxPerWeek = as.report?.maxHoursPerWeek ?? DEFAULT_MAX_HOURS_PER_WEEK;
            return getDrivingBarHours(value, absenceValue, isFullTimeUser) ? (
              <ProgressBarWithTooltip
                totalHours={value}
                absenceHours={absenceValue}
                maxHours={maxPerWeek}
                isFullTime={isFullTimeUser}
              />
            ) : (
              '-'
            );
          },
          width: 200,
        },
        {
          head: (
            <>
              <span>De senaste 28 dagarna</span>
              <FontAwesomeIcon
                icon={faInfoCircle}
                title="Beräknad körtid för uppdraget och tidigare
                  uppdrag för 28 dygn bakåt utifrån uppdragets sluttid."
              />
            </>
          ),
          css: RowCss,
          render: (as) => {
            const value = Math.max(as.report?.hoursDrivenLast28Days || 0, 0);
            const absenceValue = as.report?.absenceHoursLast28Days || 0;
            const maxPer4Weeks = as.report?.maxHoursPer4Weeks ?? DEFAULT_MAX_HOURS_PER_4_WEEKS;
            return getDrivingBarHours(value, absenceValue, isFullTimeUser) ? (
              <ProgressBarWithTooltip
                totalHours={value}
                absenceHours={absenceValue}
                maxHours={maxPer4Weeks}
                isFullTime={isFullTimeUser}
              />
            ) : (
              '-'
            );
          },
          width: 200,
        },
      ];
      return columns;
    }, [absenceReasons, absenceStatuses, isFullTimeUser]);

  const renderContent = () => {
    switch (updateCall.status) {
      case 'idle':
      case 'success':
      case 'error':
        return (
          <>
            <Container>
              <FormContainer>
                {getUserByIdCall.data &&
                  !getUserByIdCall.data.tempTestAllowedToSetOwnBreakTime && (
                    <RestrictionBanner>
                      <FontAwesomeIcon icon={faWarning} color={amber} />
                      <span>
                        <BoldSpan>
                          FT:n får INTE ange egen matrast via mobilappen.
                        </BoldSpan>{' '}
                        Matrast-väljaren visas men är inaktiverad. Justeras via
                        Globala inställningar →{' '}
                        <em>
                          Fälttestare som FÅR ange egen matrast via mobilappen
                        </em>
                        .
                      </span>
                    </RestrictionBanner>
                  )}
                <Col>
                  <Title>
                    <TextButton hoverAnimation onClick={goToPreviousDate}>
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </TextButton>
                    {availability?.dateString}
                    <TextButton hoverAnimation onClick={goToNextDate}>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </TextButton>
                  </Title>
                  {/* Användaren som modalen själv hämtat, inte useUsers-cachen: den kan vara tom
                      eller inaktuell, och då visades blocket även för heltidare. */}
                  {getUserByIdCall.data && !getUserByIdCall.data.isFullTime && (
                    <WeekAvailabilityOverview
                      date={currentDate}
                      onSelectDate={setCurrentDate}
                      userId={userId}
                    />
                  )}
                  {showUserFields && (
                    <>
                      <Row>
                        <Col>
                          <Label>Från</Label>
                          <DatePicker
                            customInput={
                              <CustomInput inputRef={startInputRef} />
                            }
                            dateFormat="HH:mm"
                            disabled={viewOnly}
                            locale="sv"
                            maxTime={new Date(0, 0, 0, 23, 59, 0)}
                            minTime={new Date(0, 0, 0, 0, 0, 0)}
                            onChange={(date) => {
                              if (date) {
                                setValue('startString', formatTime(date));
                              } else {
                                setValue('startString', undefined);
                              }
                            }}
                            selected={
                              watch('startString')
                                ? toDateFromHHmm(watch('startString')!)
                                : undefined
                            }
                            shouldCloseOnSelect
                            showTimeSelect
                            showTimeSelectOnly
                            timeCaption="Börjar"
                            timeFormat="HH:mm"
                            timeIntervals={15}
                          />
                        </Col>

                        <Col>
                          <Label>Till</Label>
                          <DatePicker
                            customInput={<CustomInput inputRef={endInputRef} />}
                            dateFormat="HH:mm"
                            disabled={viewOnly}
                            locale="sv"
                            maxTime={new Date(0, 0, 0, 23, 59, 0)}
                            minTime={new Date(0, 0, 0, 0, 0, 0)}
                            onChange={(date) => {
                              if (date) {
                                setValue('endString', formatTime(date));
                              } else {
                                setValue('endString', undefined);
                              }
                            }}
                            selected={
                              watch('endString')
                                ? toDateFromHHmm(watch('endString')!)
                                : undefined
                            }
                            shouldCloseOnSelect
                            showTimeSelect
                            showTimeSelectOnly
                            timeCaption="Slutar"
                            timeFormat="HH:mm"
                            timeIntervals={15}
                          />
                        </Col>
                      </Row>
                      {!viewOnly && (
                        <Row>
                          <Checkbox
                            checked={watch('absenceStatus') !== undefined}
                            disabled={viewOnly}
                            onChange={(e) => {
                              if (e.currentTarget.checked) {
                                setValue('startString', undefined);
                                setValue('endString', undefined);
                                setValue(
                                  'absenceStatus',
                                  AbsenceStatus.REQUESTED
                                );
                              } else {
                                setValue(
                                  'startString',
                                  availability?.originalTimes?.startString
                                );
                                setValue(
                                  'endString',
                                  availability?.originalTimes?.endString
                                );
                                setValue('absenceStatus', undefined);
                                setValue('absenceStatus', undefined);
                                setValue('absenceReason', undefined);
                              }
                            }}
                          >
                            Ej tillgänglig
                          </Checkbox>
                        </Row>
                      )}
                    </>
                  )}
                </Col>
                {watch('absenceStatus') !== undefined && (
                  <Col>
                    {watch('comment') && (
                      <Row>
                        <Col>{`Fälttestarens kommentar: ${watch('comment')}`}</Col>
                      </Row>
                    )}
                    <Row>
                      <BoldSpan style={{ fontWeight: 'bold' }}>
                        Frånvaro begärd för:
                      </BoldSpan>{' '}
                      <SearchableSelect
                        onOptionClicked={(value) => {
                          setValue('absenceReason', value as AbsenceReason);
                        }}
                        optionRender={renderAbsenceReasonOption}
                        options={absenceReasonOptions}
                        searchFilter={(str, ops) => ops}
                        selectedValue={watch('absenceReason')}
                      />
                    </Row>
                    <TextArea
                      {...register('adminComment')}
                      placeholder="Admin-kommentar"
                      style={{ width: 'auto', minHeight: 100 }}
                    />
                    {!viewOnly && (
                      <Row>
                        <AbsenceButton
                          colorDeselected={lightGrey}
                          colorSelected={lightRed}
                          icon={
                            (watch('absenceStatus') ===
                              AbsenceStatus.DECLINED && (
                              <FontAwesomeIcon icon={faTimes} />
                            )) ||
                            undefined
                          }
                          onClick={() => {
                            setValue('absenceStatus', AbsenceStatus.DECLINED);
                          }}
                          selected={
                            watch('absenceStatus') === AbsenceStatus.DECLINED
                          }
                        >
                          {absenceStatuses[AbsenceStatus.DECLINED]}
                        </AbsenceButton>
                        <AbsenceButton
                          colorDeselected={lightGrey}
                          colorSelected={lightGreen}
                          icon={
                            (watch('absenceStatus') ===
                              AbsenceStatus.APPROVED && (
                              <FontAwesomeIcon icon={faCheck} />
                            )) ||
                            undefined
                          }
                          onClick={() => {
                            setValue('absenceStatus', AbsenceStatus.APPROVED);
                          }}
                          selected={
                            watch('absenceStatus') === AbsenceStatus.APPROVED
                          }
                        >
                          {absenceStatuses[AbsenceStatus.APPROVED]}
                        </AbsenceButton>
                      </Row>
                    )}
                    {availability?.absenceStatus !== undefined &&
                      availability.absenceStatus !== null &&
                      availability.absenceApprovedOrDeclinedByID !==
                        undefined &&
                      availability.absenceApprovedOrDeclinedAt && (
                        <Row>
                          {`${absenceStatuses[availability.absenceStatus]} av
                      ${
                        users[availability.absenceApprovedOrDeclinedByID]
                          ?.name ?? '-'
                      }
                      ${
                        // Orval-mutatorn levererar fältet som Date (inte sträng);
                        // new Date(...) hanterar båda.
                        formatDateTime(
                          new Date(availability.absenceApprovedOrDeclinedAt)
                        )
                      }`}
                        </Row>
                      )}
                    {canSeeAbsenceRequestTimestamps && !!availability?.id && (
                      <AbsenceRequestTimestamps availability={availability} />
                    )}
                  </Col>
                )}
                <Col>
                  <Title>Begränsa bokningar</Title>
                  <Checkbox
                    checked={watch('isFullyBooked')}
                    disabled={viewOnly && !allowBookingRestrictions}
                    onChange={(ev) => {
                      setValue('isFullyBooked', ev.currentTarget.checked);
                    }}
                  >
                    Markera som fullbokad
                  </Checkbox>
                  <Checkbox
                    checked={watch('isOnlyAvailableForFurtherTransport')}
                    disabled={viewOnly && !allowBookingRestrictions}
                    onChange={(ev) => {
                      setValue(
                        'isOnlyAvailableForFurtherTransport',
                        ev.currentTarget.checked
                      );
                    }}
                  >
                    Kan endast bokas på vidaretransport
                  </Checkbox>
                </Col>
                <Row />
                <Row>
                  {!viewOnly && (
                    <div
                      style={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-evenly',
                      }}
                    >
                      <Button
                        onClick={handleRemoveClick}
                        style={{ display: 'flex', gap: '5px' }}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        Ta bort
                      </Button>
                      <Button onClick={handleSaveClick}>Spara</Button>
                    </div>
                  )}
                  {viewOnly && allowBookingRestrictions && (
                    <div
                      style={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button onClick={handleSaveClick}>Spara</Button>
                    </div>
                  )}
                </Row>
                {availability && getUserByIdCall.data && (
                  <WorkTimeWidget
                    availability={availability}
                    user={getUserByIdCall.data}
                    externalBookedAssignmentsCall={bookedAssignmentsCall}
                    externalRefreshBookedAssignments={refreshBookedAssignments}
                  />
                )}
              </FormContainer>
              <DrivingTimesContainer>
                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    padding: '8px 25px 0 25px',
                  }}
                >
                  <TextButton
                    onClick={() => setRightPaneView('timeline')}
                    style={{
                      fontWeight: rightPaneView === 'timeline' ? 700 : 400,
                      textDecoration:
                        rightPaneView === 'timeline' ? 'underline' : 'none',
                    }}
                  >
                    Tidslinje
                  </TextButton>
                  <TextButton
                    onClick={() => setRightPaneView('map')}
                    style={{
                      fontWeight: rightPaneView === 'map' ? 700 : 400,
                      textDecoration:
                        rightPaneView === 'map' ? 'underline' : 'none',
                    }}
                  >
                    Karta
                  </TextButton>
                </div>
                <TimeLineContainer $fillWidth={rightPaneView === 'map'}>
                  {rightPaneView === 'timeline' && assignedTimes && (
                    <AssignedTimesTimeline assignedTimes={assignedTimes} />
                  )}
                  {rightPaneView === 'map' && getUserByIdCall.data && (
                    <WorkDayMap
                      bookedAssignments={bookedAssignmentsCall.data ?? null}
                      isLoading={bookedAssignmentsCall.isLoading}
                      userId={userId}
                      date={currentDate}
                    />
                  )}
                </TimeLineContainer>
              </DrivingTimesContainer>
            </Container>
            {isDev && (
              <div
                style={{
                  margin: '12px 0',
                  padding: '8px 12px',
                  border: '1px dashed #888',
                  borderRadius: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <strong>🧪 Debug (lokalt)</strong>
                <span style={{ fontSize: 12, color: '#666' }}>
                  Kör hela auto-valideringen (regel-snurror + AI-analys) för den
                  här FT:ns uppdrag på{' '}
                  {availability?.dateString ??
                    currentDate.toLocaleDateString('sv-SE')}
                  . Återställer CompletedAnalyses-flaggorna först så snurran
                  processar uppdragen igen. Resultatet syns i
                  Standardkommentarer och Fakturera extra på respektive uppdrag.
                </span>
                <TextButton
                  onClick={handleDebugRunSnurra}
                  disabled={debugRunSnurraMutation.status === 'loading'}
                >
                  {debugRunSnurraMutation.status === 'loading'
                    ? 'Kör snurran…'
                    : 'Kör auto-validerings-snurran för dagen'}
                </TextButton>
              </div>
            )}
            {/* Externa lastbilstransportörer omfattas inte av körtidspolicyn, så
                körtidspolicy-statusen visas inte för dem (jfr körtidskolumnerna i
                tillgänglighetstabellen). Vänta in användardatan innan vi avgör — annars
                kan sektionen blinka till för en lastbilstransportör om assignedTimes
                hinner före. Samma gate (getUserByIdCall.data) som WorkTimeWidget/kartan. */}
            {assignedTimes &&
              getUserByIdCall.data &&
              !getUserByIdCall.data.isTruckTransport && (
              <>
                <Title style={{ marginBottom: '5px' }}>
                  Körtidspolicy-status
                </Title>
                <Table
                  columnSettings={columnSettings}
                  rows={assignedTimes
                    .filter((at) => at.report)
                    .sort(
                      makeNumberComparator(
                        (e) => e.startWorkTime?.getTime() ?? 0
                      )
                    )}
                />
              </>
            )}
          </>
        );

      case 'loading':
      default:
        return <LoadingSpinner>Sparar...</LoadingSpinner>;
    }
  };

  return (
    <Modal
      buttons={
        (viewOnly && !allowBookingRestrictions) ||
        updateCall.status === 'loading'
          ? []
          : [
              {
                label: 'Stäng',
                onClick: handleCloseClick,
              },
            ]
      }
      onClose={handleCloseClick}
      title={`Tillgänglighet för ${name}`}
    >
      {renderContent()}
    </Modal>
  );
};

export default AvailabilityFormModal;
